import { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { hapticFeedback, mainButton, openTelegramLink } from '@telegram-apps/sdk-react';
import type { ModuleMeta } from '../../harness/ModuleRegistry';
import { AtmosphericLayer } from '../_shared/AtmosphericLayer';
import { Sigil } from '../_nav/Sigil';
import { Wordmark } from '../_nav/Wordmark';

// 07 LET'S PLAY — final CTA.
// Igor round-3 2026-06-11:
//  – Headline → «ДАВАЙТЕ ДЕЛАТЬ РУССКИЙ ФУТБОЛ ЛУЧШЕ» with the shimmer accent.
//  – Particle "firework" burst REMOVED ("отказаться от анимации фейерверка").
//  – Content flows top-down (was justify-between) so the «Связаться» pill sits
//    higher and «Разработано в rafl.digital» stays above the fold.
//  – Section/wrapper switched to min-h-full (drop nested h-full/overflow) so it
//    plays with the gentle scroll-snap and doesn't float.
//
// CTA implementation — Igor override 2026-06-02 (WORK_LOG): in-app hover-reveal
// pill (.module-07-cta-pill) replaces the native Telegram MainButton. Brand
// colours only. The pill rests on a soft 4s drop-shadow breath
// (module-07-cta-breath) and adds a 200ms closing-flash on tap before
// openTelegramLink fires; the override stands.
//
// Igor photo block — 140×140 circle with 1.5px cyan ring + ±4px scroll parallax.
// Real photo at `/igor.jpg` if present; otherwise the SVG silhouette renders
// (TODO: drop real photo at `apps/web/public/igor.jpg`).

export const meta: ModuleMeta = {
  id: '07-lets-play',
  title: "LET'S PLAY · final CTA",
  status: 'approved',
  viewport: '390x844',
};

const CONTACT_URL = 'https://t.me/igorselenov';
const PHOTO_SRC = '/igor.jpg';

function trySync<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

// SVG head + shoulders — drawn in the sigil hairline style so the placeholder
// reads as intentional, not broken. No rotation: this stays static per brief.
function SilhouettePlaceholder() {
  return (
    <svg
      viewBox="0 0 140 140"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect width="140" height="140" fill="var(--color-surface-elevated)" />
      <circle cx="70" cy="56" r="22" fill="currentColor" />
      <path
        d="M30 140 C 30 100, 50 86, 70 86 C 90 86, 110 100, 110 140 Z"
        fill="currentColor"
      />
    </svg>
  );
}

export default function LetsPlayModule() {
  const sectionRef = useRef<HTMLElement>(null);
  const [photoOk, setPhotoOk] = useState(true);
  const [isFlashing, setIsFlashing] = useState(false);

  // Scroll-bound parallax for the photo — ±4px translateY across the section's
  // own scroll progress.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  const photoY = useTransform(scrollYProgress, [0, 1], [4, -4]);

  // Hide any leftover MainButton from a previous screen.
  useEffect(() => {
    trySync(() => {
      if (mainButton.setParams.isAvailable()) {
        mainButton.setParams({ isVisible: false });
      }
    });
  }, []);

  const handleCta = () => {
    trySync(() => hapticFeedback.impactOccurred('medium'));

    // Closing-flash before the navigation handoff — 200ms then resolve.
    setIsFlashing(true);
    window.setTimeout(() => setIsFlashing(false), 220);

    if (trySync(() => openTelegramLink.isAvailable())) {
      try {
        openTelegramLink(CONTACT_URL);
        return;
      } catch {
        /* fall through to plain navigation */
      }
    }

    // Browser fallback — open Igor's Telegram in a new tab. Silent per §1.4.
    window.setTimeout(() => {
      window.open(CONTACT_URL, '_blank', 'noopener,noreferrer');
    }, 200);
  };

  return (
    <section
      ref={sectionRef}
      className="relative min-h-full w-full bg-surface-main text-text-primary"
      style={{
        paddingTop: 'max(var(--safe-top, 0px), 0.75rem)',
        paddingBottom: 'max(var(--safe-bottom, 0px), 1.5rem)',
      }}
    >
      {/* Atmosphere sits OUTSIDE the closing-clip-path wrapper so the drift /
          spheres / shafts keep flowing while the content curtain reveals. */}
      <AtmosphericLayer spheres={3} shafts={3} intensity="full" />

      {/* Atmosphere sigil watermark — DESIGN.md §8.1 / §10 mapping for 07. */}
      <div
        className="module-07-sigil-watermark pointer-events-none absolute z-[1]"
        aria-hidden
      >
        <Sigil variant="watermark" />
      </div>

      <Wordmark variant="nav" />

      {/* Closing-curtain wrapper — clip-path inset 0 0 100% 0 → 0 over 800ms.
          Igor round-3: content flows top-down (gap, not justify-between) so the
          CTA sits higher and the attribution stays above the fold. */}
      <div
        className="module-07-closing relative z-10 flex min-h-full flex-col gap-sp-8 px-sp-6"
        style={{ paddingTop: 'calc(max(var(--safe-top, 0px), 1.25rem) + 88px)' }}
      >
        {/* Top block — photo, title, hairline divider, Igor's intro. */}
        <div className="flex flex-col gap-sp-6">
          <motion.div
            className="module-07-photo"
            style={{ y: photoY }}
            aria-label="Игорь Селенов"
          >
            {photoOk ? (
              <img
                src={PHOTO_SRC}
                alt="Игорь Селенов"
                onError={() => setPhotoOk(false)}
              />
            ) : (
              <SilhouettePlaceholder />
            )}
          </motion.div>

          <h1 className="font-display text-display leading-[1.05] tracking-[-0.02em] text-text-primary">
            <span className="motion-shimmer">
              ДАВАЙТЕ ДЕЛАТЬ РУССКИЙ ФУТБОЛ ЛУЧШЕ
            </span>
          </h1>

          <span className="module-07-hairline" aria-hidden />

          <div className="flex flex-col gap-sp-2">
            <p className="font-ui text-[1.125rem] font-medium leading-[1.3] tracking-[-0.005em] text-text-primary">
              Игорь Селенов
            </p>
            <p className="font-ui text-body-sm leading-[1.4] text-text-muted">
              Издатель, медиаменеджер
            </p>
          </div>
        </div>

        {/* CTA block — hover-reveal pill + attribution caption, sitting directly
            under the intro (raised, per Igor round-3). Pill override kept. */}
        <div className="flex flex-col items-start gap-sp-5">
          <button
            type="button"
            className={`module-07-cta-pill${isFlashing ? ' is-flashing' : ''}`}
            onClick={handleCta}
            aria-label="Связаться в Telegram"
          >
            <div className="module-07-cta-inner">Связаться</div>
            <div className="module-07-cta-circle" aria-hidden>
              <span>→</span>
            </div>
          </button>

          <p className="font-ui text-caption leading-tight text-text-primary/60">
            Разработано в rafl.digital
          </p>
        </div>
      </div>
    </section>
  );
}
